import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useState } from "react";
import Confetti from "react-confetti";

import {
  getGuaranteedPayPricingSurveyStorageKey,
  GUARANTEED_PAY_PRICING_RESPONSES,
  type GuaranteedPayPricingResponse,
} from "./constants";
import { GuaranteedPayPricingSurveyBanner } from "./GuaranteedPayPricingSurveyBanner";
import { GuaranteedPayPricingSurveyBottomSheet } from "./GuaranteedPayPricingSurveyBottomSheet";

export function GuaranteedPayPricingSurveyEntrypoint() {
  const bottomSheetModalState = useModalState();
  const { userId } = useDefinedWorker();
  const { showInfoToast } = useToast();
  const [showConfetti, setShowConfetti] = useState(false);

  const isEnabled = useCbhFlag(CbhFeatureFlag.GUARANTEED_PAY_PRICING_SURVEY, {
    defaultValue: false,
  });

  const storageKey = getGuaranteedPayPricingSurveyStorageKey(userId);

  const [pricingResponse, setPricingResponse] = useLocalStorage<
    GuaranteedPayPricingResponse | undefined
  >(storageKey, undefined);

  const shouldShowBanner = isEnabled && !isDefined(pricingResponse);

  useLogEffect(
    APP_V2_USER_EVENTS.GUARANTEED_PAY_PRICING_SURVEY_BANNER_VIEWED,
    { workerId: userId },
    { enabled: shouldShowBanner }
  );

  const handleBannerClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.GUARANTEED_PAY_PRICING_SURVEY_BANNER_CTA_CLICKED, {
      workerId: userId,
    });
    bottomSheetModalState.openModal();
  }, [userId, bottomSheetModalState]);

  const handleBannerDismiss = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.GUARANTEED_PAY_PRICING_SURVEY_BANNER_DISMISSED, {
      workerId: userId,
    });
    setPricingResponse(GUARANTEED_PAY_PRICING_RESPONSES.DISMISSED);
  }, [userId, setPricingResponse]);

  const handleSubmit = useCallback(
    (response: GuaranteedPayPricingResponse) => {
      setPricingResponse(response);

      if (response === GUARANTEED_PAY_PRICING_RESPONSES.NOT_INTERESTED) {
        showInfoToast("Thanks for your feedback on Guaranteed Pay!");
      } else {
        showInfoToast("Guaranteed Pay is on the way.");
        setShowConfetti(true);
      }
    },
    [setPricingResponse, showInfoToast]
  );

  if (!shouldShowBanner && !showConfetti) {
    return null;
  }

  return (
    <>
      {showConfetti && (
        <Confetti
          gravity={0.8}
          height={window.innerHeight}
          numberOfPieces={400}
          recycle={false}
          width={window.innerWidth}
          onConfettiComplete={() => {
            setShowConfetti(false);
          }}
        />
      )}
      {shouldShowBanner && (
        <>
          <GuaranteedPayPricingSurveyBanner
            onClick={handleBannerClick}
            onDismiss={handleBannerDismiss}
          />
          <GuaranteedPayPricingSurveyBottomSheet
            modalState={bottomSheetModalState}
            workerId={userId}
            onSubmit={handleSubmit}
          />
        </>
      )}
    </>
  );
}
